import PropTypes from 'prop-types';
import { forwardRef, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Alert, ButtonGroup, Dropdown, DropdownButton, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useClientConfig from "../hooks/clientConfigHook";
import useCredentials from "../hooks/credentialsHook";
import { useGlossary } from "../hooks/glossaryHook";
import { cdate } from "../utils/CDate";
import { TextPromptDialog } from "../utils/dialogUtlities";
import { termMatches } from "../utils/glossaryUtilities";
import { readField } from "../utils/objectUtilities";
import { dynamicFilter, dynamicSort } from "../utils/tableUtilities";
import Button from './Button';
import { DefaultButton } from "./DefaultButton";
import { Col, Row } from "./Grid";
import Icon from "./Icon";
import InvertedInfiniteScroll from "./InvertedInfiniteScroll";
import Select from "./Select";
import TextareaInput from "./TextareaInput";
import TextInput from "./TextInput";
import { nowISOString } from '../utils/dateUtils';
import { csvEncode } from '../utils/csvUtils';
import useNetwork from '../hooks/networkHook';
import pdfMake from 'pdfmake';
import useMessageDisplay from './MessageDisplay';
import { genDocDefinitionFactory, insertZeroWidthSpacesInParagraph } from '../utils/printingUtilities';
import { useNetworkState } from '../hooks/appStateHook';

const TableTemplate = ({ children }) => <Table><tbody>{children}</tbody></Table>

const TableLoadMarker = forwardRef((props, ref) => <tr ref={ref}><td>&nbsp;</td></tr>)

/**
 * A dynamically generated table to display a log or list of notes, with the ability to filter the list add new notes. 
 * @component
 */
const NotesTable = ({ notes, disabled = false, authors, onStrike, onAdd, style, emptyMessage, batchSize, tz, usernameField = 'username', noteField = 'note', rowControls, csvFilename, pdfFilename, partialOfflineSupport}) => {
    let { t } = useTranslation();
    const credentials = useCredentials();
    const clientConfig = useClientConfig();
    const glossary = useGlossary();
    const [showAutogenerated, setShowAutogenerated] = useState(!clientConfig.hideAutogenNotes);
    const [filter, setFilter] = useState(null);
    const [strikeNote, setStrikeNote] = useState(null);
    const [newNote, setNewNote] = useState(null);
    const [author, setAuthor] = useState(null);
    const network = useNetwork();
    const { showError } = useMessageDisplay();
    const { isOnline } = useNetworkState();

    useEffect(() => {
        setAuthor(credentials.username);
    }, [credentials.username])

    const makeNoteText = useCallback(note => {
        let text = note[noteField]
        const filteredMatches = termMatches(glossary, text, clientConfig.ignoreGlossaryCase);

        const breakIntoLines = text => {
            const fragments = text.split(/\r\n|\r|\n/)
            return fragments.reduce((acc, current, index, array) => {
                acc.push(current);
                if (index < array.length - 1) {
                    acc.push(<br/>);
                }
                return acc;
            }, [])
        }

        // Initialize an array to store text segments and JSX elements
        let elements = [];
        let lastIndex = 0;

        filteredMatches.forEach(match => {
            // Add the text before the matched term
            if (match.index > lastIndex) {
                elements = elements.concat(breakIntoLines(text.substring(lastIndex, match.index)));
            }

            // Add the span element for the matched term
            elements.push(<OverlayTrigger key={`${match.match}_${match.index}`} overlay={<Tooltip>{match.definitions.length > 1 ? <ul>{match.definitions.map(def => <li key={def.id}>{def.def}</li>)}</ul> : match.definitions[0].def}</Tooltip>} trigger={['hover', 'focus', 'click']}><span tabIndex={0} style={{ textDecorationLine: 'underline', textDecorationStyle: 'dotted', cursor: 'help' }}>{match.match}</span></OverlayTrigger>)

            lastIndex = match.index + match.length;
        });

        // Add any remaining text after the last match
        if (lastIndex < text.length) {
            elements = elements.concat(breakIntoLines(text.substring(lastIndex)));
        }

        return elements;
    }, [glossary, noteField, clientConfig.ignoreGlossaryCase]);

    let displayNotes = useMemo(() => {
        let displayNotes = notes.filter(n => showAutogenerated || n.autogenerated === 0 || n.autogenerated === '0' || n.autogenerated === false);
        if (filter) {
            displayNotes = dynamicFilter(displayNotes, filter);
        }
        displayNotes.sort(dynamicSort(['timestamp']));
        return displayNotes;
    }, [filter, notes, showAutogenerated]);

    const noteText = useMemo(() => displayNotes.map(note => makeNoteText(note)), [displayNotes, makeNoteText])

    let handleStrike = (reason) => {
        if (reason) {
            onStrike(strikeNote, reason, credentials.username, nowISOString());
        }
        setStrikeNote(null);
    }

    const noteStyle = note => ({textDecorationLine: note.strike_timestamp ? 'line-through' : 'none'})

    let handleAdd = (event) => {
        if ((!event || (event.code === 'Enter' && !event.shiftKey)) && newNote) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            onAdd(newNote, author);
            setNewNote(null);
            setAuthor(credentials.username);
        }
    }

    const getAuthors = _ => {
        if (!authors.includes(credentials.username)) {
            return [credentials.username].concat(authors)
        }
        return authors;
    }

    const renderItem = useCallback((note, idx) => {
        if ((typeof note.autogenerated) !== 'boolean' && (typeof note.autogenerated) !== 'string' && (typeof note.autogenerated) !== 'number') {
            throw new Error(`Unexpected type for autogenerated on note ${JSON.stringify(note)} - got ${typeof note.autogenerated}`)
        }
        const autogenerated = note.autogenerated === '1' || note.autogenerated === true || note.autogenerated === 1;
        const timestamp = <><span style={{textWrap: "nowrap"}}>{cdate(note.timestamp, tz ?? note.tz).dateFormat()}</span> <span style={{textWrap: "nowrap"}}>{cdate(note.timestamp, tz ?? note.tz).timeWithSecondsFormat(true)}</span></>
        const author = <>{readField(note, usernameField)}<br />{note.user_title ?? null}</>
        const details = <><span style={noteStyle(note)}>{noteText[idx]}</span>{note.strike_timestamp ? <div>{t('struckBy', {username: note.strike_username, title: note.strike_user_title, time: cdate(note.strike_timestamp, tz ?? note.tz).dateTimeWithSecondsFormat(true), reason: note.strike_reason})}</div> : null}</>
        const fontStyle = autogenerated ? 'font-italic font-weight-light' : 'font-weight-normal';
        return <Fragment key={note.internalid ?? note.id}>
        <tr className={`d-none d-md-table-row border-bottom-1 ${fontStyle}`}>
            <td style={noteStyle(note)}>{timestamp}</td>
            <td style={noteStyle(note)}>{author}</td>
            { rowControls && <td> {rowControls?.(note)} </td> }
            <td style={{wordBreak: "break-word"}}>{details}</td>
            {onStrike && <td className="text-end">{onStrike && !disabled && !autogenerated && !note.strike_timestamp ? <Button variant="link" onClick={() => setStrikeNote(note)}><span style={{color: 'darkred'}}>{t('strikeFromLog')}</span></Button> : null}</td>}
        </tr>
        <tr className={`d-table-row d-md-none ${fontStyle}`}>
            <td className="text-end border-0">{t('timestamp')}:</td>
            <td className="border-0" style={noteStyle(note)}>{timestamp}</td>
        </tr>
        <tr className={`d-table-row d-md-none ${fontStyle}`}>
            <td className="text-end border-0">{t('author')}:</td>
            <td className="border-0" style={noteStyle(note)}>{author}</td>
        </tr>
        <tr className={`d-table-row d-md-none ${fontStyle}`}>
            <td className="text-end border-0">{t('details')}</td>
            <td className="border-0" style={{wordBreak: "break-word"}}>{details}</td>
        </tr>
        <tr className={`d-table-row d-md-none ${fontStyle}`}>
            <td colSpan={2} className="text-end border-top-0 border-bottom p-0">
                {rowControls?.(note)}
                {onStrike && !disabled && !autogenerated && !note.strike_timestamp && <Button variant="link" onClick={() => setStrikeNote(note)}><span style={{color: 'darkred'}}>{t('strikeFromLog')}</span></Button>}
            </td>
        </tr>
        </Fragment>
    }, [usernameField, rowControls, noteText, t, onStrike, disabled, tz]);

    const exportCSV = () => {
        let csv = (['Date', 'Time', 'Author', 'Note', 'Autogenerated']).map(title => csvEncode(title)).join(',') + '\n';
        csv += notes.filter(n => showAutogenerated || n.type === null)?.map(n => 
            `${csvEncode(cdate(n.timestamp, tz ?? n.tz).dateFormat())},${csvEncode(cdate(n.timestamp, tz ?? n.tz).timeFormat())},${csvEncode(n.author)},${csvEncode(n.note)},${n.autogenerated ? 'Y' : 'N'}`
        ).join('\n');        
        network.downloadBlob(new Blob([csv], { type: 'text/csv' }), csvFilename);
    }

    const exportPdf = async () => {
        try {
            const content = [
                { text: 'Notes', style: 'header', margin: [0, 0, 0, 10] },
                {
                    table: {
                        headerRows: 1, 
                        widths: ['auto', 'auto', '*'], 
                        body: [
                            [
                                { text: 'Date', style: 'tableHeader' }, { text: 'Author', style: 'tableHeader' }, { text: 'Note', style: 'tableHeader' }],
                                ...notes.map(n => {
                                    const fontSize = 8;
                                    let style = [];
                                    if (n.autogenerated) {
                                        style.push('noteSys');
                                    } else if (n.strike_username) {
                                        style.push('noteStrike');
                                    }
                                    return [
                                        { text: cdate(n.timestamp, tz ?? n.tz).dateTimeFormat(), fontSize, style },
                                        { text: n.author || '', fontSize, style },
                                        n.strike_username ? 
                                            { stack: [
                                                { text: insertZeroWidthSpacesInParagraph(n.note) || '', fontSize, style, noWrap: false },
                                                { text: t('struckBy', {username: n.strike_username, title: n.strike_user_title, time: cdate(n.strike_timestamp, clientConfig.tz)?.dateTimeWithSecondsFormat(), reason: n.strike_reason}), style: [] }
                                            ] }
                                            : { text: insertZeroWidthSpacesInParagraph(n.note) || '', fontSize, style, noWrap: false },
                                    ]
                                })
                        ]
                    },
                }
            ];
            const data = await new Promise((resolve, reject) => {
                pdfMake.createPdf(genDocDefinitionFactory(null)(clientConfig, content), null, {
                    Roboto: {
                        normal: 'dejavusans.ttf',
                        bold: 'dejavusans-bold.ttf',
                        italics: 'dejavusans-oblique.ttf',
                        bolditalics: 'dejavusans-oblique.ttf'
                    }
                }).getBuffer(data => resolve(data));
            });
            const blob = new Blob([data], { type: 'application/pdf' });
            network.downloadBlob(blob, pdfFilename);
        } catch (error) {
            showError('Error generating PDF:', error);
        }
    };

    const appendCtrls = <Row>
        <Col md><Button variant="default" onClick={() => setShowAutogenerated(showAutogenerated => !showAutogenerated)}><Icon variant="solid" kind={showAutogenerated ? 'eye-slash' : 'eye'}></Icon>{` ${showAutogenerated ? t('hide') : t('show')} ${t('autogenerated')}`}</Button></Col>
        {(csvFilename || pdfFilename) && <Col md="auto">
            <DropdownButton className="mx-2" as={ButtonGroup} variant="default" title={t('export')}>
                {csvFilename && <Dropdown.Item onClick={exportCSV}>{t('commaSeparatedValues')}</Dropdown.Item>}
                {pdfFilename && <Dropdown.Item onClick={exportPdf}>{t('PDF')}</Dropdown.Item>}
            </DropdownButton>
        </Col>}
    </Row>
    
    return <div className="d-flex flex-column h-100">
        <TextPromptDialog showModal={!!strikeNote} placeholder={t('reason')} onClose={handleStrike} title={t('confirmNoteStrike')} message={t('noteStrikeConfirmationMessage')} maxLength={256}></TextPromptDialog>
        <div className="w-100 d-flex flex-column" style={{ flex: 1, minHeight: 0, ...style}}>
            { !isOnline && partialOfflineSupport && <Alert variant="warning">{t('offlineNotesMessage')}</Alert> } 
            <TextInput value={filter} onChange={setFilter} hideLabel={true} live={true} placeholder={t('filter', { count: 1 })} prependText={<Icon intent="filter"></Icon>} appendCtrl={appendCtrls}></TextInput>
            { displayNotes.length > 0 ? <InvertedInfiniteScroll data={displayNotes} Template={TableTemplate} LoadMarker={TableLoadMarker} renderItem={renderItem} style={{ height: 'calc(100% - 40px)' }} batchSize={batchSize}></InvertedInfiniteScroll> : emptyMessage }
        </div>
        {!disabled && onAdd && 
        <Row>
            {authors?.length ? <Col md="auto"><Select hideLabel={true} placeholder={t('author')} value={author} onChange={setAuthor} options={getAuthors()}></Select></Col> : null}
            <Col>
                <TextareaInput onKeyDown={e => handleAdd(e)} live={true} value={newNote} onChange={setNewNote} placeholder={t('newNote')} hideLabel={true} rows={3}
                    appendCtrl={<DefaultButton kind="add" disabled={!newNote || newNote.trim().length < 1} hideLabel={true} onClick={() => handleAdd()}></DefaultButton>}></TextareaInput>
            </Col>
        </Row> }
        </div>
}

NotesTable.propTypes = {

    /**
     * An array of objects representing the notes to display
     */
    notes: PropTypes.arrayOf(PropTypes.object).isRequired,
    
    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    
    /**
     * An array of potential authors for a new note, which will be provided in a dropdown.
     * If undefined or empty, the dropdown will not be displayed and the author will default to the current user
     */
    authors: PropTypes.arrayOf(PropTypes.string),

     /**
     * Function to call when a note is struck from the log
     */
     onStrike: PropTypes.func,

    /**
     * Function to call when a new note is added
     */
    onAdd: PropTypes.func.isRequired,

    /**
     * The message to display if there are no notes to render
     */
    emptyMessage: PropTypes.string,

    /**
     * The number of notes to render at a time as you scroll
     */
    batchSize: PropTypes.number,

    /**
     * The field from which to read the author of the note
     */
    usernameField: PropTypes.string,

    /**
     * The field form which to read the text of the note
     */
    noteField: PropTypes.string,

    /**
     * A style objec to be applied to the frame surrounding the note list.
     * Most commonly used to set the height of the note view
     */
    style: PropTypes.object,
    /**
     * Displays custom action controls for each row
     */
    rowControls: PropTypes.func,
     /**
      * Filename for the csv export. Must be specified to enable csv export
      */
    csvFilename: PropTypes.string
}


export default NotesTable;